import { z } from 'zod';
import { getPrettyFormattedDateTimeWithTz } from 'utils/dates';
import {
  AccessGroupActorFragment,
  AccessGroupGrantSource,
  AccessGroupHistoryType,
  AccessGroupRevokeSource,
  GetAccessGroupDetailsQuery,
  GrantedZoneState,
  ZonesMode,
} from 'apollo/generated/client-operations';

export const modeLabels: Record<ZonesMode, string> = {
  OFF: 'Off',
  SHADOW: 'Shadow',
  ENFORCE: 'Enforce',
};

export const modeDescriptions: Record<ZonesMode, string> = {
  OFF: 'Off: group configuration is saved in Odin and is not applied to hardware.',
  SHADOW:
    'Shadow: site access is preserved. Group memberships describe intended access; they do not narrow hardware access.',
  ENFORCE:
    'Enforce: access depends on active memberships, current site eligibility, the published layout and provider reconciliation.',
};

export const zoneStateLabels: Record<GrantedZoneState, string> = {
  PUBLISHED: 'Published',
  NOT_YET_PUBLISHED: 'Not yet published',
  NOT_IN_CURRENT_LAYOUT: 'Not in current layout',
};

export const membershipSourceLabels: Record<
  AccessGroupGrantSource | AccessGroupRevokeSource,
  string
> = {
  MANUAL: 'Manual',
  ENROLLMENT: 'Enrollment',
  CUTOVER: 'Enablement',
  RESTORE: 'Access restored',
  OFFBOARD: 'Offboarding',
  PERMANENT_BAN: 'Permanent ban',
  TEMPORARY_SUSPENSION: 'Temporary suspension',
  CONTRACTOR_CHANGE: 'Contractor change',
  SITE_CHANGE: 'Site change',
  GROUP_ARCHIVED: 'Group archived',
};

export const actorName = (
  actor: AccessGroupActorFragment | null | undefined,
): string =>
  [actor?.identity?.firstName, actor?.identity?.lastName]
    .filter(Boolean)
    .join(' ') || 'Unknown user';

// The site's explicit timezone keeps server and browser rendering identical.
export const formatAccessDate = (
  value: string | null | undefined,
  timeZone: string,
): string => (value ? getPrettyFormattedDateTimeWithTz(value, timeZone) : '—');

type HistoryEntry = NonNullable<
  GetAccessGroupDetailsQuery['accessGroup']
>['history'][number];
const historyLabels: Record<AccessGroupHistoryType, string> = {
  CREATED: 'Group created',
  ADOPTED: 'Avigilon group adopted',
  RENAMED: 'Group renamed',
  SET_AS_DEFAULT: 'Set as default',
  DEFAULT_REPLACED: 'Default replaced',
  ARCHIVED: 'Group archived',
  ZONE_ADDED: 'Zone added',
  ZONE_REMOVED: 'Zone removed',
};
const renamePayload = z.object({ from: z.string(), to: z.string() });
const archivePayload = z.object({
  membershipsClosed: z.number(),
  zoneGrantsRemoved: z.number(),
});
export const historyDescription = (entry: HistoryEntry): string => {
  if (
    entry.type === AccessGroupHistoryType.ZoneAdded ||
    entry.type === AccessGroupHistoryType.ZoneRemoved
  ) {
    const parsed = z
      .object({ zoneName: z.string().nullish() })
      .safeParse(entry.payload);
    if (parsed.success && parsed.data.zoneName)
      return `${historyLabels[entry.type]}: ${parsed.data.zoneName}`;
  }
  if (entry.type === AccessGroupHistoryType.DefaultReplaced) {
    const parsed = z
      .object({ byGroupName: z.string().nullish() })
      .safeParse(entry.payload);
    if (parsed.success && parsed.data.byGroupName)
      return `Default replaced by ${parsed.data.byGroupName}`;
  }
  if (entry.type === AccessGroupHistoryType.SetAsDefault) {
    const parsed = z
      .object({ replacedGroupName: z.string().nullish() })
      .safeParse(entry.payload);
    if (parsed.success && parsed.data.replacedGroupName)
      return `Set as default; replaced ${parsed.data.replacedGroupName}`;
  }
  if (entry.type === AccessGroupHistoryType.Renamed) {
    const parsed = renamePayload.safeParse(entry.payload);
    if (parsed.success)
      return `Renamed from “${parsed.data.from}” to “${parsed.data.to}”`;
  }
  if (entry.type === AccessGroupHistoryType.Archived) {
    const parsed = archivePayload.safeParse(entry.payload);
    if (parsed.success)
      return `Archived; ${parsed.data.membershipsClosed} memberships closed, ${parsed.data.zoneGrantsRemoved} zone grants removed`;
  }
  return historyLabels[entry.type];
};
